import { CdkListbox, CdkListboxModule } from '@angular/cdk/listbox';
import { OverlayModule, ViewportRuler } from '@angular/cdk/overlay';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  forwardRef,
  inject,
  input,
  OnInit,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { map, startWith, takeUntil, tap } from 'rxjs';

import {
  FORM_BASE_CONTROL_CMP,
  FormBaseControlComponent,
  setControlRelationship,
} from '@plugins/client/forms/form';
import { TextInputComponent } from '@plugins/client/forms/text-input/text-input.component';
import { CheckIconComponent } from '@plugins/client/icons/check-icon';
import { ChevronIconComponent } from '@plugins/client/icons/chevron-icon';
import { FocusContainerDirective } from '@plugins/client/overlay/focus-container.directive';
import { FocusOutsideDirective } from '@plugins/client/overlay/focus-outside.directive';
import { isArray } from '@plugins/shared/utils/assertFunctions';

import { MultiSelectOptionDirective } from './multi-select-option.directive';

@Component({
  selector: 'plugins-multi-select',
  imports: [
    CommonModule,
    OverlayModule,
    CdkListboxModule,
    FocusOutsideDirective,
    FocusContainerDirective,
    ReactiveFormsModule,
    TextInputComponent,
    ChevronIconComponent,
    CheckIconComponent,
  ],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  providers: [
    {
      provide: FORM_BASE_CONTROL_CMP,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => MultiSelectComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectComponent<T>
  extends FormBaseControlComponent<T[]>
  implements OnInit
{
  readonly options = input.required<T[]>();
  readonly selectedOptionsFormat = input<(options: T[]) => string>();
  readonly trackByOption = input<(index: number, option: T) => string | number>(
    this.defaultTrackBy.bind(this),
  );
  readonly maxSelected = input<number>();
  readonly optionsViewportMargin = input(100);
  readonly placeholder = input<string>('');

  readonly optionTemplateRef = contentChild(MultiSelectOptionDirective, {
    read: TemplateRef,
  });
  readonly input = viewChild(TextInputComponent);
  readonly focusDirective = viewChild(FocusOutsideDirective);
  readonly listbox = viewChild(CdkListbox);

  private readonly document = inject(DOCUMENT);
  private readonly viewportRuler = inject(ViewportRuler);

  readonly OPTIONS_MAX_HEIGHT = 250;
  readonly multiSelectId = `multi-select-${Math.random().toString(16).slice(2)}`;
  readonly selectedValuesControl = new FormControl('');

  optionsWidth$ = this.viewportRuler.change(300).pipe(
    startWith(null),
    map(() => {
      const inputWidth =
        this.input()?.inputRef?.nativeElement.getBoundingClientRect().width;
      return inputWidth ? `${inputWidth}px` : undefined;
    }),
  );

  readonly generateCdkSettings = computed(() => {
    return {
      viewportMargin: this.optionsViewportMargin(),
      flexibleDimensions: !this.optionsViewportMargin(),
    };
  });

  private readonly controlValue = toSignal(
    this.control.valueChanges.pipe(startWith(this.control.value)),
  );
  readonly isMaxSelected = computed(() => {
    const maxSelected = this.maxSelected();
    const controlValue = this.controlValue();
    return (
      maxSelected !== undefined &&
      controlValue &&
      controlValue.length >= maxSelected
    );
  });

  /** If body scrollHeight is less than the sum viewportMargin and maxHeight
   * flexible dimensions should be allowed and viewport margin should be 0. */
  get cdk() {
    if (
      this.document.body.scrollHeight <
      this.optionsViewportMargin() + this.OPTIONS_MAX_HEIGHT
    ) {
      return { flexibleDimensions: true, viewportMargin: 0 };
    }
    return this.generateCdkSettings();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    setControlRelationship({
      controlDirective: this.controlDirective,
      formControl: this.selectedValuesControl,
      destroy$: this.destroy$,
      onChange: this.onChange,
      emitEvent: true,
      changeDetectorRef: this.cdRef,
      writeValueToParent: false,
      inheritValidators: true,
    });
    this.control.valueChanges
      .pipe(
        startWith(this.control.getRawValue()),
        tap((value: T[] | null) => {
          const transformFn = this.selectedOptionsFormat();
          if (value && transformFn) {
            this.selectedValuesControl.setValue(transformFn(value));
          } else {
            this.selectedValuesControl.setValue(
              isArray(value) ? value.join(', ') : value,
            );
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (isDisabled) {
      this.selectedValuesControl.disable({ emitEvent: false });
    } else {
      this.selectedValuesControl.enable({ emitEvent: false });
    }
  }

  override focus() {
    return this.input()?.focus();
  }

  showOptions() {
    if (this.control.enabled && !this.focusDirective()?.focused) {
      this.focusDirective()?.toggle();
    }
  }

  onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Down':
      case 'ArrowDown':
      case 'Up':
      case 'ArrowUp':
        if (!this.focusDirective()?.focused) {
          this.focusDirective()?.toggle();
          setTimeout(() => this.listbox()?.focus());
        } else {
          this.listbox()?.focus();
        }
        break;
      default:
        event.preventDefault();
        break;
    }
  }

  isSelected(option: T) {
    return this.control.value?.includes(option);
  }

  private defaultTrackBy(index: number, option: T) {
    if (typeof option === 'string') {
      return option;
    }
    return index;
  }
}
